import './PageBackground.css';

/**
 * PageBackground — soft, blurred color blobs sitting behind page content,
 * approved after mockup review as a site-wide (but intensity-varying)
 * decorative treatment. Fixed to the viewport (not the page's scroll
 * height) so it always fills the visible area consistently regardless
 * of how long a given page's content is.
 *
 * Renders as the first child of a page's outer wrapper; that wrapper
 * needs its own solid `background` removed (this component supplies
 * the base cream color itself) but should otherwise keep its layout
 * exactly as-is — cards and text inside already have their own opaque
 * backgrounds, so legibility isn't affected by what's behind them.
 *
 * variant:
 *   'vibrant' — Dashboard: the hub, can afford to feel more alive
 *   'calm'    — Deal page: the decision moment, stays quiet
 *   'light'   — everywhere else: gentle consistency, minimal presence
 */
const BLOB_SETS = {
  vibrant: [
    { top: '-30px', left: '-20px', width: '280px', height: '280px', color: '#F4C11C', opacity: 0.18 },
    { top: '180px', left: '10%', width: '220px', height: '220px', color: '#6B7FB5', opacity: 0.14 },
    { bottom: '-60px', right: '-10px', width: '340px', height: '340px', color: '#4FA893', opacity: 0.17 },
    { top: '260px', right: '6%', width: '220px', height: '220px', color: '#F4C11C', opacity: 0.13 },
    { bottom: '160px', left: '6%', width: '180px', height: '180px', color: '#F4C978', opacity: 0.16 },
    { bottom: '220px', right: '12%', width: '200px', height: '200px', color: '#6B7FB5', opacity: 0.12 },
  ],
  calm: [
    { top: '-50px', right: '-30px', width: '240px', height: '240px', color: '#4FA893', opacity: 0.11 },
    { top: '220px', right: '8%', width: '180px', height: '180px', color: '#6B7FB5', opacity: 0.09 },
    { bottom: '-40px', left: '-20px', width: '210px', height: '210px', color: '#F4C11C', opacity: 0.10 },
    { bottom: '200px', left: '8%', width: '160px', height: '160px', color: '#F4C978', opacity: 0.09 },
  ],
  light: [
    { top: '-40px', right: '-20px', width: '180px', height: '180px', color: '#4FA893', opacity: 0.09 },
    { bottom: '-30px', left: '-10px', width: '160px', height: '160px', color: '#F4C11C', opacity: 0.08 },
  ],
};

export default function PageBackground({ variant = 'light' }) {
  // Blobs removed entirely (Aug 18, per Ali's report — a real miss
  // caught from a live screenshot: this component was never touched
  // during either the color pass or the structural rebuild, so it was
  // still painting the OLD warm-orange/blue/teal Golden Hour blobs
  // behind every page, on top of an also-still-old hardcoded cream
  // base color in PageBackground.css — completely disconnected from
  // theme.js, which is why the live background didn't match the
  // approved reference at all. Simply recoloring the blobs to navy/
  // yellow wasn't the right fix either: the new Wayfinding theme is
  // explicitly flat and undecorated everywhere else (no gradients, no
  // soft shapes) — decorative blurred blobs contradict that regardless
  // of what color they are. `variant` is accepted but unused now,
  // kept so every existing call site (<PageBackground variant="..." />
  // across Dashboard/DealScreen/etc.) keeps working with zero changes
  // needed there.
  return <div className="page-background" aria-hidden="true" />;
}